import {
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import ConfigService from '../services/config.service'
import AppConfig from '../config'
import MobileMenu from './layout/mobilemenu.component'
import AppStoreButton from './layout/appstore.component'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import { isGlobalDomain } from './helpers'

const PREFIX = 'Home'

const classes = {
  root: `${PREFIX}-root`,
  rootContainer: `${PREFIX}-root-container`,
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  gridContainer: `${PREFIX}-grid-container`,
  paper: `${PREFIX}-paper`,
  paperInner: `${PREFIX}-paper-inner`,
  logo: `${PREFIX}-logo`,
  buttonContainer: `${PREFIX}-button-container`,
  subTitle: `${PREFIX}-subtitle`,
  text: `${PREFIX}-text`,
  tips: `${PREFIX}-tips`,
  version: `${PREFIX}-version`,
}
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.rootContainer}`]: {
    maxWidth: '100%',
    flexGrow: '1 0 auto',
    minHeight: 'calc(100vh - 125px)',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4, 0, 1, 0),
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(8, 6, 1, 6),
    },
  },
  [`& .${classes.gridContainer}`]: {
    flexGrow: 1,
    display: 'flex',
    marginBottom: theme.spacing(1.5),
  },
  [`& .${classes.paper}`]: {
    padding: theme.spacing(6, 2, 2, 2),
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(0, 2),
    },
  },
  [`& .${classes.logo}`]: {
    height: theme.spacing(5),
    verticalAlign: 'middle',
    marginLeft: theme.spacing(1),
  },
  [`& .${classes.buttonContainer}`]: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
    textAlign: 'center',
    padding: '0px 20px',
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 4.5),
    },
  },
  [`& .${classes.buttonContainer} > .MuiButtonBase-root`]: {
    marginTop: theme.spacing(0.5),
  },
  [`& .${classes.title}`]: {
    verticalAlign: 'middle',
  },
  [`& .${classes.tips}`]: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(2),
    display: 'flex',
  },
  [`& .${classes.tips} > img`]: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(0),
  },
  [`& .${classes.text}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.spacing(1.4),
    },
  },
  [`& .${classes.version}`]: {
    color: theme.palette.secondary.main,
    marginLeft: '10px',
  },
  [theme.breakpoints.up('lg')]: {
    [`& .${classes.buttonContainer} button, .${classes.buttonContainer} a`]: {
      fontSize: theme.spacing(1.55),
    },
  },
}))

function Home() {
  const [open, setOpen] = useState(false)
  const [disabledPortin, setDisabledPortin] = useState(false)
  const [appNotice, setAppNotice] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const getEnv = async () => {
      const { disabledPortin, appNotice } = await ConfigService.getEnv()

      setDisabledPortin(disabledPortin || isGlobalDomain)
      setAppNotice(appNotice)
    }

    try {
      getEnv()
    } catch (err) {
      console.log(err)
    }
  }, [])

  //handle singpass callback and redirect to portin / reg based on last session
  useEffect(() => {
    const lastPath = sessionStorage.getItem('lastPath')
    if (window.location.href.indexOf('callback?') > -1) {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const errorDesc = searchParams.get('error-description')
      if (lastPath) {
        navigate(lastPath, {
          state: {
            code: code,
            session: state,
            error: error,
            errorDesc: errorDesc,
          },
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRegister = () => {
    if (!disabledPortin) {
      setOpen(true)
    } else {
      navigate('/registration')
    }
  }

  const handleOnClose = () => {
    setOpen(false)
  }

  return (
    <Root className={classes.root}>
      <div className={classes.rootContainer}>
        <MobileMenu />
        <Grid
          container
          direction="row"
          justifyContent="center"
          className={classes.gridContainer}
        >
          <Grid
            item
            xs={11}
            sm={6}
            md={5}
            lg={3}
            component={Paper}
            elevation={3}
            className={classes.paper}
          >
            <Typography variant="h2" align="center">
              <span className={classes.title}>Welcome to </span>
              <img
                className={classes.logo}
                src={`${AppConfig.PUBLIC_URL}/logo.png`}
                alt="logo"
              />
            </Typography>
            <Typography variant="h6" align="center">
              Prepaid Registration
            </Typography>
            <Container className={classes.buttonContainer}>
              <Typography variant="subtitle2" align="center">
                Ready to start?
                <br />
                Make sure you have your ID with you!
                <br />
                <br />
                <b>
                  For physical store purchases, please register your SIM within
                  7 days from date of purchase
                </b>
                <br />
                <br />
                {appNotice && appNotice !== 'false' && (
                  <>
                    <b>{appNotice}</b>
                    <br />
                    <br />
                  </>
                )}
              </Typography>
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleRegister}
              >
                New SIM Registration
              </Button>
              {!disabledPortin && (
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  component={RouterLink}
                  to="/portin"
                >
                  {'Port-In & Registration'}
                </Button>
              )}
            </Container>
            <Container sx={{ textAlign: 'center' }}>
              <Typography className={classes.text}>
                Download M1 Prepaid App to manage your account
                <br />
                <br />
              </Typography>
              <AppStoreButton />
            </Container>
          </Grid>
        </Grid>
        <Grid container direction="row" justifyContent="center">
          <Grid
            item
            xs={11}
            sm={6}
            md={5}
            lg={3}
            component={Paper}
            elevation={3}
            className={classes.tips}
          >
            <img
              className={classes.logo}
              src={`${AppConfig.PUBLIC_URL}/device_1.svg`}
              alt="logo"
            />
            <Typography className={classes.text} fontWeight={700}>
              To get the best experience, try registering your new SIM on your
              mobile phone!
            </Typography>
          </Grid>
        </Grid>
        <Dialog open={open} onClose={handleOnClose} maxWidth="xs">
          <DialogTitle>
            <Container sx={{ textAlign: 'center', pt: '50px' }}>
              <ErrorIcon color="primary" sx={{ fontSize: '80px' }} />
            </Container>
            Do you wish to port in your existing non-M1 Prepaid Number?
            <br />
            <br />
            <IconButton
              aria-label="close"
              onClick={handleOnClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="subtitle2">
              If yes, please go back to previous page and select "Port-in {'&'}{' '}
              Registration"
              <br />
              <br />
              Port-in will not be available once "Continue without port-in" is
              selected.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              p: '0px 20px 20px 20px',
            }}
          >
            <Button
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/registration"
            >
              {'Continue without port-in'}
            </Button>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              onClick={handleOnClose}
            >
              Back
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <div className={[classes.version, classes.text].join(' ')}>
        version: {AppConfig.VERSION}
      </div>
    </Root>
  )
}

export default Home
